import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { currentBusinessId, DOCUMENTS_QUERY_ROOT } from '@features/documents/useDocuments';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { listRecentDocuments } from '@data/services/documentReader';
import { cancelIssuedDocument } from '@data/services/issueTransitionService';
import {
  convertSalesOrderToDeliveryNoteDraft,
  deleteDeliveryNoteDraft,
  listDispatchableSalesOrders,
  loadDeliveryNote,
  loadSalesOrderForDelivery,
  updateDeliveryNoteDraft,
  type DeliveryNoteInput,
} from '@data/services/deliveryNoteService';

export const DELIVERY_NOTES_QUERY_ROOT = ['delivery-notes'] as const;
const USER = { id: 'local', name: 'You' };

export function useDeliveryNotes() {
  return useQuery({
    queryKey: [...DELIVERY_NOTES_QUERY_ROOT, 'list'],
    queryFn: async () => {
      const businessId = await currentBusinessId();
      return businessId
        ? listRecentDocuments(getDb() as never, businessId, 200, true, 'DELIVERY_NOTE')
        : { documents: [], total: 0, truncated: false, partyNamesByDocumentId: new Map<string, string>() };
    },
  });
}

/**
 * Issued sales orders that still have quantity left to dispatch.
 *
 * `listDispatchableSalesOrders` applies the fulfilment rule, so this list never
 * offers an order whose goods have already all left — following such a link would
 * land the user on a refusal the screen could have prevented.
 */
export function useDeliveryOrders() {
  return useQuery({
    queryKey: [...DELIVERY_NOTES_QUERY_ROOT, 'source-orders'],
    queryFn: async () => {
      const businessId = await currentBusinessId();
      if (!businessId) return [];
      return listDispatchableSalesOrders(getDb() as never, businessId);
    },
  });
}

export function useDeliverySource(salesOrderId: string | null, excludeDeliveryNoteId?: string) {
  return useQuery({
    queryKey: [...DELIVERY_NOTES_QUERY_ROOT, 'source', salesOrderId, excludeDeliveryNoteId],
    enabled: salesOrderId !== null,
    queryFn: async () => {
      if (!salesOrderId) return null;
      const business = await resolveActiveBusiness(getDb() as never);
      return loadSalesOrderForDelivery(getDb() as never, salesOrderId, {
        businessId: business.id,
        excludeDeliveryNoteId,
      });
    },
  });
}

export function useDeliveryNote(documentId: string | null) {
  return useQuery({
    queryKey: [...DELIVERY_NOTES_QUERY_ROOT, 'document', documentId],
    enabled: documentId !== null,
    queryFn: () => {
      if (!documentId) return null;
      return loadDeliveryNote(getDb() as never, documentId);
    },
  });
}

async function invalidateDeliveryNoteData(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
    client.invalidateQueries({ queryKey: DELIVERY_NOTES_QUERY_ROOT }),
    client.invalidateQueries({ queryKey: ['sales-orders'] }),
  ]);
}

export function useCreateDeliveryNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (salesOrderId: string) =>
      convertSalesOrderToDeliveryNoteDraft(getDb() as never, { salesOrderId }),
    onSuccess: () => invalidateDeliveryNoteData(client),
  });
}

export function useUpdateDeliveryNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<DeliveryNoteInput, 'salesOrderId'> }) =>
      updateDeliveryNoteDraft(getDb() as never, id, input),
    onSuccess: () => invalidateDeliveryNoteData(client),
  });
}

export function useDeleteDeliveryNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDeliveryNoteDraft(getDb() as never, id),
    onSuccess: () => invalidateDeliveryNoteData(client),
  });
}

export function useCancelDeliveryNote() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, reason }: { documentId: string; reason: string }) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db as never);
      const deviceId = await currentDeviceId(db);
      const note = await db.documents.get(documentId);
      if (!note || note.docType !== 'DELIVERY_NOTE' || note.businessId !== business.id) {
        throw new Error('This delivery note is not available in the active business.');
      }
      return db.transaction(
        'rw',
        [db.documents, db.documentLines, db.auditLogs, db.stockTransactions, db.products, db.documentLinks, db.businesses, db.syncOutbox],
        () => cancelIssuedDocument(db as never, documentId, USER, new Date().toISOString(), reason, deviceId),
      );
    },
    onSuccess: () => invalidateDeliveryNoteData(client),
  });
}
